import type { CrossReferences, ReadinessSnapshot } from '@/types/integration';
import type { ChapterStoryContext } from '@/types/storyContext';

type EventMap = {
  'manuscript:activeChapterChanged': {
    manuscriptId: string | null;
    chapterId: string | null;
  };
  'chapter:contextChanged': {
    manuscriptId: string;
    chapterId: string;
    context: ChapterStoryContext;
  };
  'character:changed': {
    manuscriptId: string;
    characterId: string;
    action: 'created' | 'updated' | 'deleted';
  };
  'relationship:changed': {
    manuscriptId: string;
    relationshipId: string;
    characterIds: string[];
    action: 'created' | 'updated' | 'deleted';
  };
  'notes:changed': {
    manuscriptId: string;
    noteId: string;
    tags: string[];
  };
  'plot:actChanged': {
    manuscriptId: string;
    actId: string;
    action: 'created' | 'updated' | 'deleted';
  };
  'aiReadiness:changed': {
    manuscriptId: string;
    system: 'characters' | 'plot' | 'world';
    entityId?: string;
    readiness: number | null;
  };
  'aiReadiness:snapshot': {
    manuscriptId: string;
    snapshot: ReadinessSnapshot;
  };
  'entity:referencesUpdated': {
    manuscriptId: string;
    entityType: string;
    entityId: string;
  };
  'entity:referencesHydrated': {
    manuscriptId: string;
    entityType: string;
    entityId: string;
    references: CrossReferences;
  };
};

type EventKey = keyof EventMap;
type EventHandler<K extends EventKey> = (payload: EventMap[K]) => void;

class EventBus {
  private listeners: Map<EventKey, Set<EventHandler<EventKey>>> = new Map();

  on<K extends EventKey>(event: K, handler: EventHandler<K>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    const handlers = this.listeners.get(event)!;
    // Cast is safe because handlers are stored per event key.
    handlers.add(handler as EventHandler<EventKey>);

    return () => {
      const registered = this.listeners.get(event);
      registered?.delete(handler as EventHandler<EventKey>);
    };
  }

  once<K extends EventKey>(event: K, handler: EventHandler<K>): () => void {
    const unsubscribe = this.on(event, (payload) => {
      unsubscribe();
      handler(payload as EventMap[K]);
    });
    return unsubscribe;
  }

  emit<K extends EventKey>(event: K, payload: EventMap[K]): void {
    const handlers = this.listeners.get(event);
    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach((handler) => {
      try {
        handler(payload as EventMap[K]);
      } catch (error) {
        console.error(`[eventBus] Handler for "${event}" failed.`, { error, payload });
      }
    });
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const eventBus = new EventBus();
export type { EventMap };

